import { AuthService, AuthUser } from '@adminprodigydesign/axis';
import {
  AfterViewInit,
  Component,
  ElementRef,
  NgZone,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import {
  OrderPayload,
  OrderServiceService,
  TermsOfService,
  SavedAddress,
  InsertAddressPayload,
} from '../../services/order-service.service';
import { Router } from '@angular/router';
import { TenantService } from '../../services/tenant.service';
import { MenuService } from '../../../features/user/menu.service';
import { Subject, takeUntil, distinctUntilChanged } from 'rxjs';
import { LatestTerms, TermsService } from '../../services/terms.service';
import { AnalyticsService } from '../../services/analytics.service';
import { CartService } from '../../services/cart.service';
import {
  BusinessHoursCalculatorService,
  BusinessHoursMeta,
} from '../../services/business-hours-calculator.service';

declare const google: any;

type OrderType = 'delivery' | 'collection';

@Component({
  selector: 'app-checkout-page',
  standalone: false,
  templateUrl: './checkout-page.component.html',
  styleUrl: './checkout-page.component.scss',
})
export class CheckoutPageComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('placeAutocompleteHost', { static: false })
  placeAutocompleteHost?: ElementRef<HTMLDivElement>;

  deliveryFeeOriginalGross = 0;
  minimumOrder = 0;
  smallOrderFee = 0;
  minimumDeliveryAmount = 0;

  checkoutForm: FormGroup;
  cartItems: any[] = [];

  currentUser: AuthUser | null = null;

  distanceMiles: number | null = null;
  private userCoords?: google.maps.LatLngLiteral;

  private geocoder?: google.maps.Geocoder;
  distanceCheckError = '';

  private manualAddrSubStarted = false;
  private isPatchingFromPlaces = false;
  private manualAddressTypingTracked = false;

  private readonly ORDER_TYPE_KEY = 'waitron.orderType';
  orderType: OrderType = 'delivery';

  private placeAutocompleteElement?: any;
  private placeAutocompleteBoundHost?: HTMLDivElement;
  private placeAutocompleteSelectHandler?: EventListener;
  private placeAutocompleteErrorHandler?: EventListener;

  private destroy$ = new Subject<void>();

  MAX_DELIVERY_MILES = 0;
  showTooFarModal = false;

  distanceLoading = false;
  private distanceRunId = 0;
  private loaderStartedAt = 0;
  private readonly MIN_LOADER_MS = 350;

  private businessHoursMeta: BusinessHoursMeta | null = null;
  businessHours: any[] = [];
  todayStoreHours?: any;
  todayDeliveryHours?: any;

  showDeliveryClosedModal = false;

  orderNotes = '';
  isEditingNotes = false;
  orderNotesDraft = '';

  latestTerms: LatestTerms | null = null;
  tosLoading = false;
  private tosLoadStarted = false;

  pauseActive = false;
  pauseClosedCollection = false;
  pauseClosedDelivery = false;
  pauseUntilLabel: string | null = null;

  showPauseOrdersModal = false;
  pauseModalType: 'delivery' | 'collection' | 'both' | null = null;

  private pausePollSub?: any;

  savedAddresses: SavedAddress[] = [];
  selectedDeliveryAddressId: number | 'new' | null = null;
  loadingSavedAddresses = false;

  submitTrigger:
    | 'button'
    | 'form_enter'
    | 'form_enter_address_autocomplete'
    | 'unknown' = 'unknown';
  submitError = '';
  addressSaveError = '';
  orderSubmitting = false;
  submitted = false;
  private redirectingToMenu = false;

  constructor(
    private fb: FormBuilder,
    private cartService: CartService,
    private orderService: OrderServiceService,
    private router: Router,
    private tenantService: TenantService,
    private menuService: MenuService,
    private auth: AuthService,
    private zone: NgZone,
    private termsService: TermsService,
    private analytics: AnalyticsService,
    private businessHoursCalc: BusinessHoursCalculatorService,
  ) {
    this.checkoutForm = this.fb.group({
      fName: [
        '',
        [
          Validators.required,
          this.noWhitespaceValidator(),
          Validators.minLength(2),
          Validators.maxLength(50),
        ],
      ],
      lName: [
        '',
        [
          Validators.required,
          this.noWhitespaceValidator(),
          Validators.minLength(2),
          Validators.maxLength(50),
        ],
      ],
      email: [
        '',
        [
          Validators.required,
          this.noWhitespaceValidator(),
          Validators.maxLength(100),
          this.trimmedEmailValidator(),
        ],
      ],
      phone: [
        '',
        [
          Validators.required,
          this.noWhitespaceValidator(),
          this.phoneValidator(),
        ],
      ],

      // Address controls (validators applied only when Delivery)
      addressLine1: [''],
      addressLine2: ['', [Validators.maxLength(100)]],
      city: ['', [Validators.maxLength(60)]],
      county: ['', [Validators.maxLength(60)]],
      postalCode: ['', [Validators.maxLength(12)]],
      placeId: [''],
      formattedAddress: [''],
      lat: [''],
      lng: [''],
    });

    // Keep cartItems up to date
    this.cartService.cart$.pipe(takeUntil(this.destroy$)).subscribe((items) => {
      this.cartItems = items || [];

      if (!this.cartItems.length) {
        this.redirectToMenu();
        return;
      }

      this.redirectingToMenu = false;

      if (!this.checkoutBegun && this.cartItems.length) {
        this.checkoutBegun = true;
        this.analytics.event('begin_checkout', {
          currency: 'GBP',
          value: this.checkoutTotal,
          items: this.gaItemsFromCart(),
        });
      }
    });
  }

  ngOnInit(): void {
    if (this.redirectingToMenu) return;

    this.loadLatestTerms();

    // 1) Load order type from session
    const saved = this.getSavedOrderType();
    if (saved) this.orderType = saved;

    // 2) Apply address validators according to order type
    this.applyAddressValidators();

    this.auth.user$.pipe(takeUntil(this.destroy$)).subscribe((user) => {
      const wasLoggedIn = !!this.currentUser;
      this.currentUser = user;
      this.prefillCheckoutFromUser(user);

      if (user) {
        this.loadSavedAddresses();
      } else if (wasLoggedIn) {
        this.savedAddresses = [];
        this.selectedDeliveryAddressId = null;
      }
    });

    this.refreshPauseStatusPublic();
    this.pausePollSub = setInterval(
      () => this.refreshPauseStatusPublic(),
      90_000,
    );

    // 3) Load delivery rules & tenant fees
    this.menuService
      .getPublicDeliveryRules()
      .pipe(takeUntil(this.destroy$))
      .subscribe((rule) => {
        if (rule?.deliveryRules) {
          this.deliveryFeeOriginalGross = Number(
            rule.deliveryRules.deliveryCharge ?? 0,
          );
          this.minimumDeliveryAmount = Number(
            rule.deliveryRules.minimumDeliveryAmount ?? 0,
          );
        }
      });

    this.orderNotes = this.cartService.getOrderNotes();

    this.tenantService.businessHoursMeta$
      .pipe(takeUntil(this.destroy$))
      .subscribe((meta) => {
        this.businessHoursMeta = meta;
      });

    this.tenantService.businessHours$
      .pipe(takeUntil(this.destroy$))
      .subscribe((hours) => {
        if (!hours) return;

        this.businessHours = hours;

        const timeZone = this.businessHoursCalc.getTimezone(
          this.businessHoursMeta,
        );
        const now = this.businessHoursCalc.getNow(this.businessHoursMeta);

        const todayHours = this.businessHoursCalc.getTodayHours(
          hours,
          timeZone,
          now,
        );

        this.todayStoreHours = todayHours.todayStoreHours;
        this.todayDeliveryHours = todayHours.todayDeliveryHours;

        if (!this.isDeliveryOpenNow && this.orderType === 'delivery') {
          this.orderType = 'collection';
          this.saveOrderType('collection');
          this.applyAddressValidators();
        }
      });

    this.tenantService.tenantConfig$
      .pipe(takeUntil(this.destroy$))
      .subscribe((cfg) => {
        if (!cfg) return;
        this.minimumOrder = cfg.tenant.minimumOrderAmount;
        this.smallOrderFee = cfg.tenant.smallOrderFee;
        this.MAX_DELIVERY_MILES = cfg.tenant.maxDistance;
      });
  }

  private prefillCheckoutFromUser(user: AuthUser | null): void {
    if (!user) return;

    this.setIfAllowed('fName', user.fName ?? '');
    this.setIfAllowed('lName', user.lName ?? '');
    this.setIfAllowed('email', user.email ?? user.userEmail ?? '');
    this.setIfAllowed('phone', user.phone ?? user.phoneNumber ?? '');
  }

  private setIfAllowed(
    controlName: 'fName' | 'lName' | 'email' | 'phone',
    incomingValue: string,
  ): void {
    const control = this.checkoutForm.get(controlName);
    if (!control) return;

    const nextValue = (incomingValue || '').trim();
    if (!nextValue) return;

    const currentValue = String(control.value || '').trim();
    const shouldPatch = !currentValue || control.pristine;

    if (!shouldPatch) return;

    control.patchValue(nextValue, { emitEvent: false });
  }

  setOrderType(type: OrderType) {
    if (type === 'delivery' && this.pauseActive && this.pauseClosedDelivery) {
      this.pauseModalType = 'delivery';
      this.orderType = 'collection';
      this.saveOrderType('collection');
      this.applyAddressValidators();
      this.showPauseOrdersModal = true;
      return;
    }

    if (
      type === 'collection' &&
      this.pauseActive &&
      this.pauseClosedCollection
    ) {
      this.pauseModalType = 'collection';

      if (!this.pauseClosedDelivery) {
        this.orderType = 'delivery';
        this.saveOrderType('delivery');
        this.applyAddressValidators();
        this.showPauseOrdersModal = true;
        return;
      }

      this.pauseModalType = 'both';
      this.showPauseOrdersModal = true;
      return;
    }

    if (type === 'delivery' && !this.isDeliveryOpenNow) {
      this.onAttemptDeliveryWhileClosed();
      return;
    }

    if (this.orderType === type) return;

    this.orderType = type;
    this.saveOrderType(type);
    this.applyAddressValidators();

    if (this.isDelivery && this.isLoggedInUser) {
      if (this.savedAddresses.length > 0) {
        if (
          this.selectedDeliveryAddressId === null ||
          this.selectedDeliveryAddressId === 'new'
        ) {
          this.selectedDeliveryAddressId = this.savedAddresses[0].id;
        }
        this.patchSelectedSavedAddress();
      } else {
        this.selectedDeliveryAddressId = 'new';
      }
    }

    if (!this.isDelivery) {
      this.checkoutForm.patchValue({
        addressLine1: '',
        addressLine2: '',
        city: '',
        county: '',
        postalCode: '',
        placeId: '',
        formattedAddress: '',
        lat: '',
        lng: '',
      });

      this.distanceMiles = null;
      this.userCoords = undefined;
      this.showTooFarModal = false;
      this.destroyPlaceAutocompleteElement();

      return;
    }

    if (this.distanceMiles != null && this.isOutOfDeliveryArea) {
      this.analytics.event('checkout_open_out_of_area_modal', {
        source: 'toggle_to_delivery',
        distance_miles: this.distanceMiles,
        max_delivery_miles: this.MAX_DELIVERY_MILES,
      });
      this.showTooFarModal = true;
    }

    setTimeout(() => {
      this.tryInitPlacesAutocomplete();
      this.startManualAddressWatcher();
    }, 0);

    this.showPauseOrdersModal = this.isOrderingPausedForCurrentType;

    this.analytics.event('checkout_order_type_change', {
      order_type: type,
      delivery_open: this.isDeliveryOpenNow,
    });
  }

  onAttemptDeliveryWhileClosed() {
    // Ensure we’re visually on collection
    if (this.orderType !== 'collection') {
      this.orderType = 'collection';
      this.saveOrderType('collection');
      this.applyAddressValidators();
    }

    this.showDeliveryClosedModal = true;

    this.analytics.event('checkout_delivery_closed_block', {
      attempted: 'delivery',
    });
  }

  onCloseDeliveryClosedModal() {
    this.showDeliveryClosedModal = false;
    if (this.orderType !== 'collection') {
      this.orderType = 'collection';
      this.saveOrderType('collection');
      this.applyAddressValidators();
    }
  }

  // You already have this on Menu; add here if not present
  formatTime(time: string | null): string {
    if (!time) return '';
    const [hh, mm] = time.split(':').map((v) => parseInt(v, 10));
    const ampm = hh >= 12 ? 'PM' : 'AM';
    const h12 = hh % 12 || 12;
    return `${h12}:${mm.toString().padStart(2, '0')} ${ampm}`;
  }

  get isDelivery(): boolean {
    return this.orderType === 'delivery';
  }

  private getSavedOrderType(): OrderType | null {
    try {
      const val = sessionStorage.getItem(this.ORDER_TYPE_KEY);
      return val === 'delivery' || val === 'collection' ? val : null;
    } catch {
      return null;
    }
  }

  private saveOrderType(val: OrderType) {
    try {
      sessionStorage.setItem(this.ORDER_TYPE_KEY, val);
    } catch {}
  }

  private applyAddressValidators() {
    const a1 = this.checkoutForm.get('addressLine1')!;
    const city = this.checkoutForm.get('city')!;
    const county = this.checkoutForm.get('county')!;
    const pc = this.checkoutForm.get('postalCode')!;
    const placeId = this.checkoutForm.get('placeId')!;
    const formattedAddress = this.checkoutForm.get('formattedAddress')!;
    const lat = this.checkoutForm.get('lat')!;
    const lng = this.checkoutForm.get('lng')!;

    if (this.isDelivery) {
      a1.setValidators([
        Validators.required,
        this.noWhitespaceValidator(),
        Validators.maxLength(100),
      ]);

      city.setValidators([Validators.maxLength(60)]);
      county.setValidators([Validators.maxLength(60)]);
      pc.setValidators([Validators.maxLength(12)]);

      placeId.setValidators([]);
      formattedAddress.setValidators([]);
      lat.setValidators([]);
      lng.setValidators([]);
    } else {
      a1.clearValidators();
      city.clearValidators();
      county.clearValidators();
      pc.clearValidators();
      placeId.clearValidators();
      formattedAddress.clearValidators();
      lat.clearValidators();
      lng.clearValidators();
    }

    a1.updateValueAndValidity({ emitEvent: false });
    city.updateValueAndValidity({ emitEvent: false });
    county.updateValueAndValidity({ emitEvent: false });
    pc.updateValueAndValidity({ emitEvent: false });
    placeId.updateValueAndValidity({ emitEvent: false });
    formattedAddress.updateValueAndValidity({ emitEvent: false });
    lat.updateValueAndValidity({ emitEvent: false });
    lng.updateValueAndValidity({ emitEvent: false });
  }

  ngAfterViewInit(): void {
    if (this.redirectingToMenu) return;

    this.tryInitPlacesAutocomplete();
    this.startManualAddressWatcher();
  }

  private redirectToMenu(): void {
    if (this.redirectingToMenu) return;

    this.redirectingToMenu = true;
    this.router.navigate(['/menu'], { replaceUrl: true });
  }

  onSubmit(): void {
    if (this.orderSubmitting) {
      this.trackSubmitBlocked('order_submitting');
      return;
    }

    this.submitted = true;

    this.analytics.event('place_order_click', {
      order_type: this.orderType,
      is_delivery: this.isDelivery,
      cart_items: this.cartItems?.length ?? 0,
      value: this.checkoutTotal,
    });

    this.submitError = '';
    this.addressSaveError = '';
    this.normaliseCustomerFields();

    if (this.pauseActive && this.isOrderingPausedForCurrentType) {
      this.showPauseOrdersModal = true;
      return;
    }

    if (this.checkoutForm.invalid) {
      const invalidFieldErrors = this.getInvalidFieldErrors(this.checkoutForm);
      const invalidFields = invalidFieldErrors.map((x) => x.field);
      const invalidErrors = invalidFieldErrors.map(
        (x) => `${x.field}:${x.errors.join('|')}`,
      );

      this.checkoutForm.markAllAsTouched();

      this.submitError =
        'Please correct the highlighted fields before continuing.';

      this.analytics.event('checkout_form_invalid', {
        order_type: this.orderType,
        is_delivery: this.isDelivery,

        submit_trigger: this.submitTrigger,
        checkout_blockers: this.getCheckoutBlockers().join(','),

        invalid_fields: invalidFields.join(','),
        invalid_errors: invalidErrors.join(','),
        invalid_field_count: invalidFields.length,

        checkout_mode: this.isLoggedInUser ? 'logged_in' : 'guest',
        using_saved_address: this.isUsingSavedDeliveryAddress,
        adding_new_address: this.isAddingNewAddress,
        has_saved_addresses: this.hasSavedAddresses,

        has_resolved_delivery_address: this.hasResolvedDeliveryAddress,
        has_usable_delivery_address: this.hasUsableDeliveryAddress,

        distance_miles_known: this.distanceMiles != null,
        distance_miles: this.distanceMiles ?? -1,
        max_delivery_miles: this.MAX_DELIVERY_MILES,
        distance_loading: this.distanceLoading,
        has_distance_error: !!this.distanceCheckError,

        is_below_minimum: this.isBelowMinimum,
        amount_remaining_to_minimum: this.amountRemainingToMinimum,
        minimum_delivery_amount: this.minimumDeliveryAmount,
        minimum_order: this.minimumOrder,

        cart_items: this.cartItems?.length ?? 0,
        cart_value: this.checkoutTotal,

        screen_width: window.innerWidth,
        screen_height: window.innerHeight,
      });

      invalidFieldErrors.forEach((item) => {
        this.analytics.event('checkout_invalid_field', {
          field_name: item.field,
          field_errors: item.errors.join('|'),
          order_type: this.orderType,
          checkout_mode: this.isLoggedInUser ? 'logged_in' : 'guest',
          submit_trigger: this.submitTrigger,
          using_saved_address: this.isUsingSavedDeliveryAddress,
          adding_new_address: this.isAddingNewAddress,
        });
      });

      this.submitTrigger = 'unknown';

      return;
    }

    if (this.distanceLoading) {
      this.blockSubmit(
        'Please wait while we confirm your delivery distance.',
        'distance_loading',
      );
      return;
    }

    if (this.distanceCheckError) {
      this.blockSubmit(this.distanceCheckError, 'distance_check_error');
      return;
    }

    if (!this.hasUsableDeliveryAddress) {
      this.blockSubmit(
        'Please select your delivery address from the Google suggestions so we can confirm delivery range.',
        'no_usable_delivery_address',
      );
      return;
    }

    if (this.needsDistanceCheck) {
      this.blockSubmit(
        'Please wait while we confirm your delivery address is in range.',
        'needs_distance_check',
      );
      return;
    }

    if (this.isOutOfDeliveryArea) {
      this.analytics.event('checkout_blocked_out_of_area', {
        distance_miles: this.distanceMiles,
        max_delivery_miles: this.MAX_DELIVERY_MILES,
      });
      this.showTooFarModal = true;
      return;
    }

    if (this.isBelowMinimum) {
      this.blockSubmit(
        `Add £${this.amountRemainingToMinimum.toFixed(2)} more to reach the minimum delivery amount.`,
        'below_minimum',
      );
      return;
    }

    if (this.tosLoading) {
      this.blockSubmit(
        'Please wait while we load the latest terms of service.',
        'terms_loading',
      );
      return;
    }

    const tos = this.buildTermsOfServiceOrNull();
    if (!tos) {
      this.submitError =
        'We could not load the latest terms of service. Please try again.';
      this.loadLatestTerms(true);
      return;
    }

    const vals = this.checkoutForm.getRawValue();
    const {
      placeId,
      formattedAddress,
      lat,
      lng,
      addressLine1,
      addressLine2,
      city,
      county,
      postalCode,
      ...guestUser
    } = vals;

    const addressBlock = this.isDelivery
      ? {
          addressLine1,
          addressLine2,
          city,
          county,
          country: 'UK',
          postalCode,
        }
      : null;

    const googleAddressBlock = this.isDelivery
      ? {
          placeId,
          formattedAddress,
          lat: Number(lat) || null,
          lng: Number(lng) || null,
        }
      : null;

    const basket: any = {
      items: this.cartItems.map((item) => ({
        id: Number(item.itemId),
        quantity: item.quantity,
        options: item.options || [],
      })),
      termsOfService: tos,
      promotion: [],
      deliveryType: this.orderType,
    };

    if (!this.isLoggedInUser) {
      basket.guestUser = this.buildGuestUserPayload(
        guestUser,
        addressBlock,
        googleAddressBlock,
      );
    }

    const rawNotes = sessionStorage.getItem('waitron.orderNotes') || '';
    const notes = rawNotes.trim().slice(0, 500);
    if (notes) {
      basket.comment = notes;
    }

    // Logged-in + delivery + saved address selected
    if (
      this.isLoggedInUser &&
      this.isDelivery &&
      this.selectedDeliveryAddressId !== null &&
      this.selectedDeliveryAddressId !== 'new'
    ) {
      basket.address = {
        deliveryId: Number(this.selectedDeliveryAddressId),
      };

      this.submitOrderRequest({
        action: 'insertOrder',
        // debug: 'CSD',
        basket,
      });

      return;
    }

    // Logged-in + delivery + add new address first
    if (this.isLoggedInUser && this.isDelivery) {
      const insertAddressPayload: InsertAddressPayload = {
        action: 'insertAddress',
        foodOrderAddress: {
          addressLine1: String(addressLine1 || '').trim(),
          addressLine2: String(addressLine2 || '').trim(),
          city: String(city || '').trim(),
          county: String(county || '').trim(),
          country: 'UK',
          postalCode: String(postalCode || '').trim(),
        },
      };

      this.orderService.insertAddress(insertAddressPayload).subscribe({
        next: (addressResponse) => {
          const insertSucceeded =
            addressResponse?.status === 'success' &&
            addressResponse?.link?.status === 'success';

          if (!insertSucceeded) {
            this.addressSaveError =
              addressResponse?.link?.message ||
              addressResponse?.message ||
              'We could not save your delivery address. Please check it and try again.';
            return;
          }

          const deliveryId = Number(addressResponse?.address?.id);

          if (!Number.isFinite(deliveryId)) {
            this.addressSaveError =
              'Your address was saved, but we could not continue checkout. Please try again.';
            return;
          }

          basket.address = { deliveryId };

          this.savedAddresses = [
            ...this.savedAddresses,
            {
              id: deliveryId,
              addressLine1: String(addressLine1 || '').trim(),
              addressLine2: String(addressLine2 || '').trim(),
              city: String(city || '').trim(),
              county: String(county || '').trim(),
              country: 'UK',
              postalCode: String(postalCode || '').trim(),
            },
          ];
          this.selectedDeliveryAddressId = deliveryId;

          this.submitOrderRequest({
            action: 'insertOrder',
            // debug: 'CSD',
            basket,
          });
        },
        error: (err) => {
          console.error('Insert address failed', err);
          this.addressSaveError =
            err?.message ||
            'Failed to save delivery address. Please try again.';
        },
      });

      return;
    }

    // Guest checkout or logged-in collection
    this.submitOrderRequest({
      action: 'insertOrder',
      // debug: 'CSD',
      basket,
    });
  }

  onCheckoutEnterKey(event: Event): void {
    const target = event.target as HTMLElement | null;
    const tagName = target?.tagName?.toLowerCase();

    if (
      tagName === 'button' ||
      tagName === 'select' ||
      tagName === 'textarea'
    ) {
      return;
    }

    const insideGoogleAutocomplete = target?.closest?.(
      '.google-place-autocomplete',
    );

    if (insideGoogleAutocomplete) {
      this.submitTrigger = 'form_enter_address_autocomplete';
      return;
    }

    this.submitTrigger = 'form_enter';
  }

  onTooFarModalClosed() {
    this.showTooFarModal = false;
  }

  acceptTooFarSwitchToCollection() {
    this.analytics.event('checkout_switch_to_collection', {
      reason: 'out_of_area',
      distance_miles: this.distanceMiles,
    });

    this.showTooFarModal = false;
    this.setOrderType('collection');
  }

  keepDeliveryEditAddress() {
    this.showTooFarModal = false;
    this.focusPlaceAutocomplete();
  }

  ngOnDestroy() {
    this.destroyPlaceAutocompleteElement();

    this.destroy$.next();
    this.destroy$.complete();

    if (this.pausePollSub) clearInterval(this.pausePollSub);
  }

  startEditNotes() {
    this.analytics.event('checkout_notes_edit_start', {
      has_existing_notes: !!this.orderNotes,
    });

    this.orderNotesDraft = this.orderNotes || '';
    this.isEditingNotes = true;
  }

  cancelEditNotes() {
    this.analytics.event('checkout_notes_cancel', {});
    this.isEditingNotes = false;
  }

  saveNotesFromCheckout() {
    const trimmed = this.cartService.saveOrderNotes(this.orderNotesDraft);
    this.orderNotes = trimmed;

    if (trimmed) {
      this.analytics.event('checkout_notes_save', { length: trimmed.length });
    } else {
      this.analytics.event('checkout_notes_clear', {});
    }

    this.isEditingNotes = false;
  }

  private async updateDistanceFromShop(runId?: number) {
    if (!this.userCoords) return;

    const myRun = runId ?? ++this.distanceRunId;

    if (!runId) {
      this.startFormLoader();
      this.distanceMiles = null;
    }

    try {
      const { miles } = await this.tenantService.distanceMatrixFromShop(
        this.userCoords,
      );

      if (myRun === this.distanceRunId) {
        this.distanceMiles = miles;
        await this.stopFormLoaderIfLatest(myRun);

        this.analytics.event('checkout_distance_check_success', {
          ...this.getCheckoutAnalyticsContext(),
          distance_miles: Number(miles?.toFixed(2)),
          max_delivery_miles: this.MAX_DELIVERY_MILES,
          is_out_of_area: this.isOutOfDeliveryArea,
        });

        if (this.isOutOfDeliveryArea) {
          this.analytics.event('checkout_out_of_delivery_area', {
            distance_miles: this.distanceMiles,
            max_delivery_miles: this.MAX_DELIVERY_MILES,
          });
          this.showTooFarModal = true;
        }
      }
    } catch (err) {
      console.warn('Distance calc failed:', err);

      if (myRun === this.distanceRunId) {
        this.distanceMiles = null;
        await this.stopFormLoaderIfLatest(myRun);

        this.analytics.event('checkout_distance_check_failed', {
          ...this.getCheckoutAnalyticsContext(),
          reason: 'distance_matrix_failed',
        });
      }
    }
  }

  private placeTick?: any;

  private async tryInitPlacesAutocomplete() {
    const hostEl = this.placeAutocompleteHost?.nativeElement;

    if (!this.isDelivery || !hostEl || this.isUsingSavedDeliveryAddress) {
      return;
    }

    const g = (window as any).google;

    if (!g?.maps?.importLibrary) {
      console.warn(
        'Google Maps importLibrary not loaded. Check your script tag/key.',
      );

      this.analytics.event('checkout_address_autocomplete_init_failed', {
        ...this.getCheckoutAnalyticsContext(),
        reason: 'google_import_library_missing',
      });

      return;
    }

    // If already bound to this exact host, do nothing.
    if (
      this.placeAutocompleteElement &&
      this.placeAutocompleteBoundHost === hostEl
    ) {
      return;
    }

    this.destroyPlaceAutocompleteElement();

    try {
      const placesLibrary = await g.maps.importLibrary('places');
      const PlaceAutocompleteElement =
        placesLibrary.PlaceAutocompleteElement ||
        g.maps.places.PlaceAutocompleteElement;

      if (!PlaceAutocompleteElement) {
        console.warn(
          'PlaceAutocompleteElement is not available. Make sure Places API (New) is enabled.',
        );

        this.analytics.event('checkout_address_autocomplete_init_failed', {
          ...this.getCheckoutAnalyticsContext(),
          reason: 'place_autocomplete_element_missing',
        });

        return;
      }

      const autocompleteEl = new PlaceAutocompleteElement({
        includedRegionCodes: ['gb'],
        includedPrimaryTypes: ['street_address', 'premise', 'subpremise'],
      });

      autocompleteEl.placeholder = 'Start typing your address';

      this.placeAutocompleteSelectHandler = ((event: any) => {
        this.zone.run(async () => {
          await this.handlePlaceAutocompleteSelect(event);
        });
      }) as EventListener;

      this.placeAutocompleteErrorHandler = ((event: any) => {
        console.warn('Google Place Autocomplete error:', event);

        this.zone.run(() => {
          this.distanceCheckError =
            'We could not load address suggestions. Please try again.';

          this.analytics.event('checkout_address_autocomplete_error', {
            ...this.getCheckoutAnalyticsContext(),
            reason: 'gmp_error',
          });
        });
      }) as EventListener;

      autocompleteEl.addEventListener(
        'gmp-select',
        this.placeAutocompleteSelectHandler,
      );

      autocompleteEl.addEventListener(
        'gmp-error',
        this.placeAutocompleteErrorHandler,
      );

      hostEl.innerHTML = '';
      hostEl.appendChild(autocompleteEl);

      this.placeAutocompleteElement = autocompleteEl;
      this.placeAutocompleteBoundHost = hostEl;

      this.analytics.event('checkout_address_autocomplete_ready', {
        ...this.getCheckoutAnalyticsContext(),
      });
    } catch (err) {
      console.warn('Failed to initialise PlaceAutocompleteElement:', err);

      this.analytics.event('checkout_address_autocomplete_init_failed', {
        ...this.getCheckoutAnalyticsContext(),
        reason: 'init_exception',
      });
    }
  }

  private async handlePlaceAutocompleteSelect(event: any): Promise<void> {
    if (!this.isDelivery) return;

    const placePrediction = event?.placePrediction;

    if (!placePrediction?.toPlace) {
      console.warn('No place prediction returned from autocomplete selection.');

      this.analytics.event('checkout_address_select_failed', {
        ...this.getCheckoutAnalyticsContext(),
        reason: 'missing_place_prediction',
      });

      return;
    }

    const place = placePrediction.toPlace();

    try {
      await place.fetchFields({
        fields: [
          'id',
          'formattedAddress',
          'location',
          'addressComponents',
          'displayName',
        ],
      });
    } catch (err) {
      console.warn('Failed to fetch selected place fields:', err);

      this.analytics.event('checkout_address_select_failed', {
        ...this.getCheckoutAnalyticsContext(),
        reason: 'fetch_fields_failed',
      });

      this.distanceCheckError =
        'We could not read this address. Please choose another address.';

      return;
    }

    if (!place?.location) {
      this.distanceCheckError =
        'We could not read this address location. Please choose another address.';

      this.analytics.event('checkout_address_select_failed', {
        ...this.getCheckoutAnalyticsContext(),
        reason: 'missing_location',
      });

      return;
    }

    const lat =
      typeof place.location.lat === 'function'
        ? place.location.lat()
        : place.location.lat;

    const lng =
      typeof place.location.lng === 'function'
        ? place.location.lng()
        : place.location.lng;

    this.userCoords = { lat, lng };

    const comps = place.addressComponents || [];

    const get = (type: string) =>
      comps.find((component: any) => component.types?.includes(type))
        ?.longText || '';

    const postalCode = get('postal_code');
    const city = get('postal_town') || get('locality');
    const county =
      get('administrative_area_level_2') || get('administrative_area_level_1');

    const line1 =
      [get('street_number'), get('route')].filter(Boolean).join(' ') ||
      place.formattedAddress ||
      place.displayName ||
      '';

    this.distanceCheckError = '';

    this.isPatchingFromPlaces = true;

    try {
      this.checkoutForm.patchValue({
        addressLine1: line1,
        city,
        county,
        postalCode,
        placeId: place.id || '',
        formattedAddress: place.formattedAddress || '',
        lat: this.userCoords.lat,
        lng: this.userCoords.lng,
      });

      this.checkoutForm.get('addressLine1')?.markAsTouched();
      this.checkoutForm.get('addressLine1')?.updateValueAndValidity({
        emitEvent: false,
      });
    } finally {
      this.isPatchingFromPlaces = false;
    }

    this.analytics.event('checkout_address_selected', {
      ...this.getCheckoutAnalyticsContext(),
      has_line_1: !!line1,
      has_city: !!city,
      has_county: !!county,
      has_postal_code: !!postalCode,
      has_place_id: !!place.id,
      has_formatted_address: !!place.formattedAddress,
      has_lat_lng: !!this.userCoords?.lat && !!this.userCoords?.lng,
    });

    this.manualAddressTypingTracked = false;

    const myRun = ++this.distanceRunId;
    this.startFormLoader();
    this.distanceMiles = null;

    await this.updateDistanceFromShop(myRun);

    if (!this.shippingInfoSent && !this.isOutOfDeliveryArea) {
      this.shippingInfoSent = true;

      this.analytics.event('add_shipping_info', {
        currency: 'GBP',
        value: this.checkoutTotal,
        delivery_type: 'delivery',
        items: this.gaItemsFromCart(),
      });
    }
  }

  private destroyPlaceAutocompleteElement(): void {
    if (this.placeAutocompleteElement && this.placeAutocompleteSelectHandler) {
      this.placeAutocompleteElement.removeEventListener(
        'gmp-select',
        this.placeAutocompleteSelectHandler,
      );
    }

    if (this.placeAutocompleteElement && this.placeAutocompleteErrorHandler) {
      this.placeAutocompleteElement.removeEventListener(
        'gmp-error',
        this.placeAutocompleteErrorHandler,
      );
    }

    if (this.placeAutocompleteBoundHost) {
      this.placeAutocompleteBoundHost.innerHTML = '';
    }

    this.placeAutocompleteElement = undefined;
    this.placeAutocompleteBoundHost = undefined;
    this.placeAutocompleteSelectHandler = undefined;
    this.placeAutocompleteErrorHandler = undefined;
  }

  private focusPlaceAutocomplete(): void {
    const el = this.placeAutocompleteElement;

    if (!el) return;

    if (typeof el.focus === 'function') {
      el.focus();
      return;
    }

    const input = el.shadowRoot?.querySelector?.(
      'input',
    ) as HTMLInputElement | null;

    input?.focus();
  }

  get isOutOfDeliveryArea(): boolean {
    return (
      this.isDelivery &&
      this.distanceMiles != null &&
      this.distanceMiles > this.MAX_DELIVERY_MILES
    );
  }

  get isDeliveryOpenNow(): boolean {
    if (!this.businessHours?.length) return false;

    const timeZone = this.businessHoursCalc.getTimezone(this.businessHoursMeta);
    const now = this.businessHoursCalc.getNow(this.businessHoursMeta);

    const activeDeliveryHour = this.businessHoursCalc.getActiveBusinessHourNow(
      this.businessHours,
      'delivery',
      timeZone,
      now,
    );

    if (activeDeliveryHour) {
      this.todayDeliveryHours = activeDeliveryHour;
      return true;
    }

    return false;
  }

  private startFormLoader() {
    this.zone.run(() => {
      if (!this.distanceLoading) {
        this.distanceLoading = true;
        this.loaderStartedAt = performance.now();
      }
    });
  }

  private async stopFormLoaderIfLatest(runId: number) {
    if (runId !== this.distanceRunId) return;
    const elapsed = performance.now() - this.loaderStartedAt;
    const wait = Math.max(0, this.MIN_LOADER_MS - elapsed);
    if (wait) await new Promise((r) => setTimeout(r, wait));
    if (runId !== this.distanceRunId) return;

    this.zone.run(() => {
      this.distanceLoading = false;
    });
  }

  loadLatestTerms(force = false) {
    if (this.tosLoadStarted && !force) return;
    this.tosLoadStarted = true;

    if (force) {
      this.termsService.clearCache();
    }

    this.tosLoading = true;
    this.termsService
      .prefetchLatestTerms()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (t) => {
          this.latestTerms = t;
          this.tosLoading = false;
        },
        error: (err) => {
          console.warn('Failed to load latest terms of service', err);
          this.latestTerms = null;
          this.tosLoading = false;
          this.analytics.event('checkout_terms_load_failed', {
            force,
          });
        },
      });
  }

  private blockSubmit(message: string, blocker: string): void {
    this.submitError = message;
    this.trackSubmitBlocked(blocker);
  }

  private trackSubmitBlocked(blocker: string): void {
    this.analytics.event('checkout_submit_blocked', {
      ...this.getCheckoutAnalyticsContext(),
      blocker,
      checkout_blockers: this.getCheckoutBlockers().join(','),
      submit_trigger: this.submitTrigger,
    });

    this.submitTrigger = 'unknown';
  }

  private buildTermsOfServiceOrNull(): TermsOfService | null {
    if (!this.latestTerms) return null;

    const version = String(this.latestTerms.versions || '').trim();
    if (!version) return null;

    return { accepted: 1, version };
  }

  private isClosedVal(v: any): boolean {
    return v === 1 || v === '1' || v === true;
  }

  get serviceFee(): number {
    return this.tenantService.serviceFee;
  }

  private gaItemsFromCart() {
    return (this.cartItems || []).map((ci: any) => ({
      item_id: String(ci.itemId ?? ci.id ?? ''),
      item_name: String(ci.name ?? ''),
      item_variant: this.extractVariant(ci.name),
      price: Number(ci.price ?? 0),
      quantity: Number(ci.quantity ?? 1),
      item_category: ci.categoryName || undefined,
    }));
  }

  private extractVariant(name: string): string | undefined {
    const m = /\(([^)]+)\)\s*$/.exec(name || '');
    return m?.[1];
  }

  openTooFarModalFromInline() {
    this.analytics.event('checkout_open_out_of_area_modal', {
      distance_miles: this.distanceMiles,
    });
    this.showTooFarModal = true;
  }

  private checkoutBegun = false;
  private shippingInfoSent = false;

  private startManualAddressWatcher() {
    if (this.manualAddrSubStarted) return;
    this.manualAddrSubStarted = true;

    this.checkoutForm
      .get('addressLine1')
      ?.valueChanges.pipe(takeUntil(this.destroy$), distinctUntilChanged())
      .subscribe((value) => {
        if (!this.isDelivery) return;
        if (this.isPatchingFromPlaces) return;
        if (this.isUsingSavedDeliveryAddress) return;

        const addressLine1 = String(value || '').trim();

        if (addressLine1.length >= 3 && !this.manualAddressTypingTracked) {
          this.manualAddressTypingTracked = true;

          this.analytics.event('checkout_address_manual_typing', {
            ...this.getCheckoutAnalyticsContext(),
            has_address_text: true,
          });
        }

        this.checkoutForm.patchValue(
          {
            placeId: '',
            formattedAddress: '',
            lat: '',
            lng: '',
            city: '',
            county: '',
            postalCode: '',
            addressLine2: '',
          },
          { emitEvent: false },
        );

        this.userCoords = undefined;
        this.distanceMiles = null;
        this.showTooFarModal = false;
      });
  }

  get needsDistanceCheck(): boolean {
    return this.isDelivery && this.distanceMiles == null;
  }

  private extractPauseDto(res: any) {
    const list = res?.data;
    if (!Array.isArray(list) || list.length === 0) return null;
    return list[0] ?? null;
  }

  formatTimeHHmm(time: string | null | undefined): string | null {
    if (!time) return null;
    const m = /^(\d{2}):(\d{2})(?::\d{2})?$/.exec(time.trim());
    if (!m) return null;
    return `${m[1]}:${m[2]}`;
  }

  private computePausedType(): 'delivery' | 'collection' | 'both' | null {
    if (!this.pauseActive) return null;
    if (this.pauseClosedDelivery && this.pauseClosedCollection) return 'both';
    if (this.pauseClosedDelivery) return 'delivery';
    if (this.pauseClosedCollection) return 'collection';
    return null;
  }

  get isOrderingPausedForCurrentType(): boolean {
    if (!this.pauseActive) return false;
    return this.orderType === 'delivery'
      ? this.pauseClosedDelivery
      : this.pauseClosedCollection;
  }

  get isAllOrderingPaused(): boolean {
    return (
      this.pauseActive && this.pauseClosedDelivery && this.pauseClosedCollection
    );
  }

  onClosePauseOrdersModal() {
    this.showPauseOrdersModal = false;
  }

  private refreshPauseStatusPublic() {
    this.menuService
      .viewOrderPausePublic()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          if (!res || res.status !== 'success') {
            this.pauseActive = false;
            this.pauseClosedCollection = false;
            this.pauseClosedDelivery = false;
            this.pauseUntilLabel = null;
            this.pauseModalType = null;
            this.showPauseOrdersModal = false;
            return;
          }

          const dto = this.extractPauseDto(res);

          if (!dto) {
            this.pauseActive = false;
            this.pauseClosedCollection = false;
            this.pauseClosedDelivery = false;
            this.pauseUntilLabel = null;
            this.pauseModalType = null;
            this.showPauseOrdersModal = false;
            return;
          }

          this.pauseActive = true;
          this.pauseClosedCollection = this.isClosedVal(dto.isClosedCollection);
          this.pauseClosedDelivery = this.isClosedVal(dto.isClosedDelivery);
          this.pauseUntilLabel = this.formatTimeHHmm(dto.pauseUntil ?? null);
          this.pauseModalType = this.computePausedType();

          if (
            this.orderType === 'delivery' &&
            this.pauseClosedDelivery &&
            !this.pauseClosedCollection
          ) {
            this.orderType = 'collection';
            this.saveOrderType('collection');
            this.applyAddressValidators();
          }

          this.showPauseOrdersModal = this.isOrderingPausedForCurrentType;
        },
        error: () => {
          this.pauseActive = false;
          this.pauseClosedCollection = false;
          this.pauseClosedDelivery = false;
          this.pauseUntilLabel = null;
          this.pauseModalType = null;
          this.showPauseOrdersModal = false;
        },
      });
  }

  private get isLoggedInUser(): boolean {
    return !!this.currentUser;
  }

  private buildGuestUserPayload(
    guestUser: any,
    addressBlock: any,
    googleAddressBlock: any,
  ) {
    return {
      ...guestUser,
      userEmail: guestUser.email,
      phoneNumber: guestUser.phone,
      ...(addressBlock ? { address: addressBlock } : {}),
      ...(googleAddressBlock ? { googleAddress: googleAddressBlock } : {}),
    };
  }

  goToLogin(): void {
    const vals = this.checkoutForm.getRawValue();

    this.analytics.event('checkout_login_prompt_click', {
      destination: 'login',
    });

    this.router.navigate(['/login'], {
      queryParams: {
        returnUrl: '/checkout',
        email: vals.email || null,
        fName: vals.fName || null,
        lName: vals.lName || null,
        phone: vals.phone || null,
      },
    });
  }

  goToRegister(): void {
    const vals = this.checkoutForm.getRawValue();

    this.analytics.event('checkout_login_prompt_click', {
      destination: 'register',
    });

    this.router.navigate(['/register'], {
      queryParams: {
        returnUrl: '/checkout',
        email: vals.email || null,
        fName: vals.fName || null,
        lName: vals.lName || null,
        phone: vals.phone || null,
      },
    });
  }

  get hasSavedAddresses(): boolean {
    return this.savedAddresses.length > 0;
  }

  get isUsingSavedDeliveryAddress(): boolean {
    return (
      this.isLoggedInUser &&
      this.isDelivery &&
      this.selectedDeliveryAddressId !== null &&
      this.selectedDeliveryAddressId !== 'new'
    );
  }

  private loadSavedAddresses(): void {
    if (!this.currentUser) {
      this.savedAddresses = [];
      this.selectedDeliveryAddressId = null;
      return;
    }

    this.loadingSavedAddresses = true;

    this.orderService.viewAddress().subscribe({
      next: (response) => {
        this.loadingSavedAddresses = false;

        if (response?.status !== 'success') {
          this.savedAddresses = [];
          this.selectedDeliveryAddressId = 'new';
          return;
        }

        const list = this.extractSavedAddresses(response);
        this.savedAddresses = list;

        if (list.length > 0) {
          const existingSelected = Number(this.selectedDeliveryAddressId);
          const stillExists = list.some((a) => a.id === existingSelected);

          if (!stillExists) {
            this.selectedDeliveryAddressId = list[0].id;
          }

          if (this.isDelivery) {
            this.patchSelectedSavedAddress();
          }
        } else {
          this.selectedDeliveryAddressId = 'new';
        }
      },
      error: (err) => {
        console.error('Failed to load saved addresses', err);
        this.loadingSavedAddresses = false;
        this.savedAddresses = [];
        this.selectedDeliveryAddressId = 'new';
      },
    });
  }

  private extractSavedAddresses(response: any): SavedAddress[] {
    const raw = response?.data;

    if (!raw) return [];

    const list = Array.isArray(raw) ? raw : [raw];

    return list
      .map((addr: any) => ({
        id: Number(addr?.addressId ?? addr?.id ?? addr?.deliveryId),
        addressLine1: String(addr?.addressLine1 ?? '').trim(),
        addressLine2: String(addr?.addressLine2 ?? '').trim(),
        city: String(addr?.city ?? '').trim(),
        county: String(addr?.county ?? '').trim(),
        country: String(addr?.country ?? 'UK').trim(),
        postalCode: String(addr?.postalCode ?? '').trim(),
      }))
      .filter(
        (addr: SavedAddress) =>
          Number.isFinite(addr.id) &&
          !!addr.addressLine1 &&
          !!addr.city &&
          !!addr.postalCode,
      );
  }

  onDeliveryAddressSelectionChange(value: number | 'new'): void {
    this.selectedDeliveryAddressId = value;

    this.analytics.event('checkout_saved_address_selection_change', {
      ...this.getCheckoutAnalyticsContext(),
      selected_address_type: value === 'new' ? 'new' : 'saved',
    });

    if (value === 'new') {
      this.checkoutForm.patchValue(
        {
          addressLine1: '',
          addressLine2: '',
          city: '',
          county: '',
          postalCode: '',
          placeId: '',
          formattedAddress: '',
          lat: '',
          lng: '',
        },
        { emitEvent: true },
      );

      this.userCoords = undefined;
      this.distanceMiles = null;
      this.showTooFarModal = false;

      // Clear old autocomplete binding because the host is about to be recreated
      this.destroyPlaceAutocompleteElement();

      setTimeout(() => {
        this.tryInitPlacesAutocomplete();
        this.startManualAddressWatcher();
        this.focusPlaceAutocomplete();
      }, 0);

      return;
    }

    this.destroyPlaceAutocompleteElement();
    this.patchSelectedSavedAddress();
  }

  private patchSelectedSavedAddress(): void {
    if (
      !this.isDelivery ||
      this.selectedDeliveryAddressId === null ||
      this.selectedDeliveryAddressId === 'new'
    ) {
      return;
    }

    const selected = this.savedAddresses.find(
      (a) => a.id === this.selectedDeliveryAddressId,
    );

    if (!selected) return;

    this.distanceCheckError = '';

    this.checkoutForm.patchValue(
      {
        addressLine1: selected.addressLine1 || '',
        addressLine2: selected.addressLine2 || '',
        city: selected.city || '',
        county: selected.county || '',
        postalCode: selected.postalCode || '',
        placeId: '',
        formattedAddress: this.formatSavedAddress(selected),
        lat: '',
        lng: '',
      },
      { emitEvent: true },
    );

    this.userCoords = undefined;
    this.distanceMiles = null;
    this.showTooFarModal = false;

    this.checkDistanceForSavedAddress(selected);
  }

  private checkDistanceForSavedAddress(address: SavedAddress): void {
    if (!this.isDelivery) return;

    const g = (window as any).google;

    if (!g?.maps?.Geocoder) {
      console.warn('Google Geocoder not loaded.');
      this.distanceCheckError =
        'We could not check this saved address. Please choose a new address from the Google suggestions.';
      this.distanceMiles = null;

      this.analytics.event('checkout_saved_address_distance_failed', {
        ...this.getCheckoutAnalyticsContext(),
        reason: 'geocoder_not_loaded',
      });

      return;
    }

    this.geocoder = this.geocoder || new g.maps.Geocoder();

    const geocoder = this.geocoder;

    if (!geocoder) {
      this.distanceCheckError =
        'We could not check this saved address. Please choose a new address from the Google suggestions.';
      this.distanceMiles = null;

      this.analytics.event('checkout_saved_address_distance_failed', {
        ...this.getCheckoutAnalyticsContext(),
        reason: 'geocoder_create_failed',
      });

      return;
    }

    const fullAddress = [
      address.addressLine1,
      address.addressLine2,
      address.city,
      address.county,
      address.postalCode,
      address.country || 'UK',
    ]
      .filter(Boolean)
      .join(', ');

    const myRun = ++this.distanceRunId;
    this.startFormLoader();
    this.distanceMiles = null;
    this.distanceCheckError = '';

    geocoder.geocode(
      {
        address: fullAddress,
        componentRestrictions: { country: 'GB' },
      },
      (results, status) => {
        this.zone.run(async () => {
          if (myRun !== this.distanceRunId) return;

          const result = results?.[0];

          if (
            status !== google.maps.GeocoderStatus.OK ||
            !result?.geometry?.location
          ) {
            await this.stopFormLoaderIfLatest(myRun);

            this.userCoords = undefined;
            this.distanceMiles = null;
            this.distanceCheckError =
              'We could not check this saved address. Please choose a new address from the Google suggestions.';

            this.analytics.event('checkout_saved_address_distance_failed', {
              ...this.getCheckoutAnalyticsContext(),
              reason: 'geocode_failed',
              geocode_status: String(status),
            });

            return;
          }

          const loc = result.geometry.location;

          this.userCoords = {
            lat: loc.lat(),
            lng: loc.lng(),
          };

          this.checkoutForm.patchValue(
            {
              formattedAddress: result.formatted_address || fullAddress,
              lat: this.userCoords.lat,
              lng: this.userCoords.lng,
            },
            { emitEvent: false },
          );

          await this.updateDistanceFromShop(myRun);

          this.analytics.event('checkout_saved_address_distance_success', {
            ...this.getCheckoutAnalyticsContext(),
          });

          if (!this.shippingInfoSent && !this.isOutOfDeliveryArea) {
            this.shippingInfoSent = true;
            this.analytics.event('add_shipping_info', {
              currency: 'GBP',
              value: this.checkoutTotal,
              delivery_type: 'delivery',
              items: this.gaItemsFromCart(),
            });
          }
        });
      },
    );
  }

  formatSavedAddress(address: SavedAddress): string {
    return [
      address.addressLine1,
      address.addressLine2,
      address.city,
      address.county,
      address.postalCode,
    ]
      .filter(Boolean)
      .join(', ');
  }

  private submitOrderRequest(payload: OrderPayload): void {
    const vals = this.checkoutForm.getRawValue();
    this.submitError = '';
    this.orderSubmitting = true;

    this.orderService.insertOrder(payload).subscribe({
      next: (response) => {
        this.orderSubmitting = false;

        if (response.status === 'success') {
          const orderId = Number(response?.basket?.orderId);

          if (Number.isFinite(orderId)) {
            sessionStorage.setItem(
              'lastOrderCode',
              response?.basket?.publicOrderCode,
            );
            sessionStorage.setItem('lastOrderId', String(orderId));
            sessionStorage.setItem(
              'lastBasket',
              JSON.stringify(response.basket || {}),
            );
            sessionStorage.setItem(
              'clientSecret',
              response.basket.clientSecret,
            );
            sessionStorage.setItem(
              'publishableKey',
              response.basket.publishableKey,
            );
            sessionStorage.setItem(
              'connectedAccount',
              response.basket.connectedAccount,
            );

            const clientEmail =
              this.currentUser?.email ||
              this.currentUser?.userEmail ||
              vals.email ||
              '';

            if (clientEmail) {
              sessionStorage.setItem('clientEmail', clientEmail);
            }
          }

          const totalAmount = this.checkoutTotal;
          sessionStorage.setItem('totalAmount', String(totalAmount));

          this.analytics.event('checkout_order_created', {
            order_id: String(orderId),
            public_order_code: response?.basket?.publicOrderCode ?? '',
            value: totalAmount,
            order_type: this.orderType,
            checkout_mode: this.isLoggedInUser ? 'logged_in' : 'guest',
          });

          this.cartService.savePaymentSummary({
            items: this.cartItems.map((item) => ({
              id: String(item.id),
              itemId: Number(item.itemId),
              name: String(item.name),
              price: Number(item.price),
              quantity: Number(item.quantity),
              imageUrl: item.imageUrl ?? '',
              options: Array.isArray(item.options) ? item.options : [],
              extras: Array.isArray(item.extras)
                ? item.extras.map((extra: any) => ({
                    group: String(extra.group ?? ''),
                    choice: String(extra.choice ?? ''),
                    price: Number(extra.price ?? 0),
                  }))
                : [],
            })),
            orderNotes: this.orderNotes,
            orderType: this.orderType,
            deliveryFee: this.deliveryFeeOriginalGross,
            serviceFee: this.serviceFee,
            smallOrderFee: this.checkoutSmallOrderFee,
            total: this.checkoutTotal,
          });

          this.router.navigate(['/payment'], {
            state: {
              clientSecret: response.basket.clientSecret,
              publishableKey: response.basket.publishableKey,
              connectedAccount: response.basket.connectedAccount,
              orderId,
              totalAmount,
            },
          });
        } else {
          this.submitError =
            response?.message ||
            'We could not create your order. Please try again.';
        }
      },
      error: (err) => {
        this.orderSubmitting = false;
        console.error('Order failed', err);
        this.submitError =
          err?.message || 'Failed to place order. Please try again.';
      },
    });
  }

  get isIdentityReadOnly(): boolean {
    return !!this.currentUser;
  }

  get isAddingNewAddress(): boolean {
    return (
      this.isDelivery &&
      (!this.currentUser ||
        this.selectedDeliveryAddressId === 'new' ||
        this.selectedDeliveryAddressId === null)
    );
  }

  get cartSummary() {
    return this.cartService.getSummary({
      items: this.cartItems,
      orderType: this.orderType,
      deliveryFee: this.deliveryFeeOriginalGross,
      serviceFee: this.serviceFee,
      minimumOrderAmount: this.minimumOrder,
      minimumDeliveryAmount: this.minimumDeliveryAmount,
      smallOrderFee: this.smallOrderFee,
    });
  }

  get subtotal(): number {
    return this.cartSummary.subtotal;
  }

  get checkoutSmallOrderFee(): number {
    return this.cartSummary.smallOrderFee;
  }

  get checkoutTotal(): number {
    return this.cartSummary.total;
  }

  get minimumGuardTotal(): number {
    return this.cartSummary.minimumGuardTotal;
  }

  get isBelowMinimum(): boolean {
    return this.cartSummary.isBelowMinimum;
  }

  get amountRemainingToMinimum(): number {
    return this.cartSummary.amountRemainingToMinimum;
  }

  breadcrumb(): void {
    this.router.navigate(['/menu']);
  }

  private noWhitespaceValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = String(control.value ?? '');
      return value.trim().length ? null : { whitespace: true };
    };
  }

  private phoneValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const raw = String(control.value ?? '').trim();

      if (!raw) return null;

      if (!/^\d+$/.test(raw)) {
        return { invalidPhone: true };
      }

      if (raw.length < 10 || raw.length > 15) {
        return { invalidPhone: true };
      }

      return null;
    };
  }

  private trimmedEmailValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const raw = String(control.value ?? '').trim();

      if (!raw) return null; // let required handle empty

      // Use Angular's built-in email validator logic on trimmed value
      const emailRegex =
        /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/;

      return emailRegex.test(raw) ? null : { email: true };
    };
  }

  isFieldInvalid(controlName: string): boolean {
    const control = this.checkoutForm.get(controlName);
    return !!control && control.invalid && (control.touched || this.submitted);
  }

  getFieldError(controlName: string): string {
    const control = this.checkoutForm.get(controlName);
    if (!control || !control.errors) return '';

    if (control.errors['required']) return 'This field is required.';
    if (control.errors['whitespace']) return 'This field cannot be blank.';
    if (control.errors['minlength'])
      return 'Please enter at least 2 characters.';
    if (control.errors['maxlength']) return 'This value is too long.';
    if (control.errors['email']) return 'Please enter a valid email address.';
    if (control.errors['invalidPhone'])
      return 'Please enter a valid phone number.';
    return 'Please check this field.';
  }

  private normaliseCustomerFields(): void {
    const patch: any = {};
    [
      'fName',
      'lName',
      'email',
      'phone',
      'addressLine1',
      'addressLine2',
      'city',
      'county',
      'postalCode',
    ].forEach((key) => {
      const value = this.checkoutForm.get(key)?.value;
      if (typeof value === 'string') {
        patch[key] = value.trim();
      }
    });

    this.checkoutForm.patchValue(patch, { emitEvent: false });
  }

  onPhoneInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const cleaned = input.value.replace(/\D/g, '').slice(0, 15);

    if (input.value !== cleaned) {
      input.value = cleaned;
    }

    this.checkoutForm.get('phone')?.setValue(cleaned, {
      emitEvent: false,
    });
  }

  get hasResolvedDeliveryAddress(): boolean {
    if (!this.isDelivery || this.isUsingSavedDeliveryAddress) return false;

    const placeId = String(
      this.checkoutForm.get('placeId')?.value || '',
    ).trim();
    const lat = String(this.checkoutForm.get('lat')?.value || '').trim();
    const lng = String(this.checkoutForm.get('lng')?.value || '').trim();

    return !!placeId || (!!lat && !!lng);
  }

  get canShowResolvedAddressFields(): boolean {
    return (
      this.isDelivery &&
      (!this.currentUser || this.isAddingNewAddress) &&
      this.hasResolvedDeliveryAddress
    );
  }

  get hasUsableDeliveryAddress(): boolean {
    if (!this.isDelivery) return true;

    if (this.isUsingSavedDeliveryAddress) {
      return (
        this.selectedDeliveryAddressId !== null &&
        this.selectedDeliveryAddressId !== 'new'
      );
    }

    return this.hasResolvedDeliveryAddress;
  }

  private getInvalidFieldErrors(
    form: FormGroup,
    parentKey = '',
  ): Array<{ field: string; errors: string[] }> {
    const result: Array<{ field: string; errors: string[] }> = [];

    Object.keys(form.controls).forEach((key) => {
      const control = form.get(key);
      const fieldPath = parentKey ? `${parentKey}.${key}` : key;

      if (control instanceof FormGroup) {
        result.push(...this.getInvalidFieldErrors(control, fieldPath));
        return;
      }

      if (control?.invalid && control.errors) {
        result.push({
          field: fieldPath,
          errors: Object.keys(control.errors),
        });
      }
    });

    return result;
  }

  private getCheckoutBlockers(): string[] {
    const blockers: string[] = [];

    if (this.checkoutForm.invalid) blockers.push('form_invalid');
    if (this.distanceLoading) blockers.push('distance_loading');
    if (this.orderSubmitting) blockers.push('order_submitting');
    if (this.isOutOfDeliveryArea) blockers.push('out_of_delivery_area');
    if (this.tosLoading) blockers.push('terms_loading');
    if (this.needsDistanceCheck) blockers.push('needs_distance_check');
    if (this.isBelowMinimum) blockers.push('below_minimum');
    if (!this.hasUsableDeliveryAddress)
      blockers.push('no_usable_delivery_address');
    if (this.distanceCheckError) blockers.push('distance_check_error');
    if (this.pauseActive && this.isOrderingPausedForCurrentType) {
      blockers.push('ordering_paused');
    }

    return blockers;
  }

  private getCheckoutAnalyticsContext() {
    return {
      order_type: this.orderType,
      is_delivery: this.isDelivery,
      checkout_mode: this.isLoggedInUser ? 'logged_in' : 'guest',

      using_saved_address: this.isUsingSavedDeliveryAddress,
      adding_new_address: this.isAddingNewAddress,
      has_saved_addresses: this.hasSavedAddresses,

      has_resolved_delivery_address: this.hasResolvedDeliveryAddress,
      has_usable_delivery_address: this.hasUsableDeliveryAddress,

      distance_miles_known: this.distanceMiles != null,
      distance_loading: this.distanceLoading,
      has_distance_error: !!this.distanceCheckError,

      cart_items: this.cartItems?.length ?? 0,
      cart_value: this.checkoutTotal,

      screen_width: window.innerWidth,
      screen_height: window.innerHeight,
    };
  }

}
